import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const formatCustomerCreatedDate = (dateString) => {
  if (!dateString) return null;
  const localDate = new Date(String(dateString).replace(/-/g, "/"));
  if (Number.isNaN(localDate.getTime())) {
    const trimmed = String(dateString).trim();
    return trimmed || null;
  }
  return localDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ py: 1.5 }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "#64748b",
        fontSize: "0.75rem",
        fontWeight: 600,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: value ? "#1e293b" : "#94a3b8",
        fontSize: "0.875rem",
        fontStyle: value ? "normal" : "italic",
        wordBreak: "break-word",
      }}
    >
      {value || "Not available"}
    </Typography>
  </Box>
);

const CustomerDetailsDialog = ({ open, onClose, customer }) => (
  <Modal
    open={open && !!customer}
    onClose={onClose}
    aria-labelledby="customer-details-dialog-title"
  >
    <Paper
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: 440 },
        maxWidth: 440,
        bgcolor: "background.paper",
        borderRadius: "8px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        border: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#F5F5F5",
          padding: "16px 24px",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        }}
      >
        <Typography
          id="customer-details-dialog-title"
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#000",
            fontSize: "1.125rem",
          }}
        >
          {customer?.customer_name || "Customer"}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            color: "#000",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <DetailRow
          label="Created"
          value={formatCustomerCreatedDate(customer?.created_date)}
        />
        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <DetailRow
          label="Email"
          value={(customer?.orgprimaryemail ?? "").toString().trim() || null}
        />
        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <DetailRow
          label="Phone"
          value={
            (customer?.phonenumber ?? customer?.phoneNumber ?? "").toString().trim() ||
            null
          }
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Paper>
  </Modal>
);

CustomerDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerDetailsDialog;
